import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { hasPermission } from '../lib/permissions';
import { FeatureGate } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { Textarea } from '../components/ui/textarea';
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  User,
  Calendar,
  Search,
  Filter,
  ChevronRight,
  Eye,
  Check,
  X as XIcon,
  Users,
  ChevronDown,
  CalendarDays,
  Plus,
  Pencil,
  History,
  Trash2,
  DollarSign,
  Loader2,
  FileText,
  AlertTriangle,
  RotateCcw,
  Upload,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import LeaveRequestsTab from '../components/LeaveRequestsTab';
import PayrollExport from '../components/PayrollExport';
import { TimeBlockEditor, TimeBlockReadOnly } from '../components/TimeBlockEditor';
import { formatTime as formatTimeUtil } from '../utils/dateFormatting';
import { format, parseISO } from 'date-fns';
import { usePayrollTrackingContext } from '../hooks/usePayrollTrackingContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-500 hover:bg-gray-600', icon: Clock },
  started: { label: 'In Progress', color: 'bg-blue-500 hover:bg-blue-600', icon: Clock },
  completed: { label: 'Completed', color: 'bg-yellow-500 hover:bg-yellow-600', icon: AlertCircle },
  submitted: { label: 'Awaiting Approval', color: 'bg-orange-500 hover:bg-orange-600', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-green-500 hover:bg-green-600', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-500 hover:bg-red-600', icon: XCircle }
};

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES_5 = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const PAY_CYCLE_LABELS = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly'
};

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function calcBlockHoursUtil(start, end, breakMins = 0) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMin = sh * 60 + sm;
  let endMin = eh * 60 + em;
  if (endMin <= startMin) endMin += 24 * 60;
  return Math.max(0, (endMin - startMin - breakMins) / 60);
}

function PayrollConnectionPicker({ payrollCtx, testId }) {
  if (!payrollCtx?.connections?.length || payrollCtx.connections.length <= 1) return null;
  return (
    <div className="flex items-center gap-2 mb-2">
      <Label className="text-[10px] text-muted-foreground">Payroll connection:</Label>
      <Select value={payrollCtx.connectionId || ''} onValueChange={payrollCtx.setSelectedConnectionId}>
        <SelectTrigger className="h-7 text-xs w-[200px]" data-testid={testId}>
          <SelectValue placeholder="Select connection..." />
        </SelectTrigger>
        <SelectContent>
          {payrollCtx.connections.map(c => (
            <SelectItem key={c.id} value={c.id}>
              {c.label || c.company_name || `${c.provider === 'xero' ? 'Xero' : 'MYOB'} — ${c.id.slice(0, 6)}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const Timesheets = () => {
  const { api, user } = useAuth();
  const { hasFeature } = useTier();
  const canAccessPayroll = hasPermission(user, 'payroll.read');
  const [topTab, setTopTab] = useState('timesheets');
  const [activeTab, setActiveTab] = useState('all');
  const [groupedData, setGroupedData] = useState({ pay_cycle_type: 'weekly', pay_cycle_day: 1, groups: [] });
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [timeFormat, setTimeFormat] = useState('24h');
  const [acctStatus, setAcctStatus] = useState({ xero: false, myob: false });
  const [payrollConns, setPayrollConns] = useState([]);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const payrollCtx = usePayrollTrackingContext();
  
  // Group drawer state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [expandedOperator, setExpandedOperator] = useState(null);
  
  // Individual timesheet view state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [docketCrossRef, setDocketCrossRef] = useState(null);
  const [docketCrossRefLoading, setDocketCrossRefLoading] = useState(false);

  // Delete timesheet state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    totalHours: 0
  });

  // Stable ref for hasFeature to avoid re-fetch loops
  const hasFeatureRef = useRef(hasFeature);
  hasFeatureRef.current = hasFeature;

  const fetchTimesheets = useCallback(async () => {
    if (!hasFeatureRef.current('timesheets')) {
      setLoading(false);
      return;
    }
    if (activeTab === 'payroll-export') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const statusParam = activeTab === 'all' ? '' : activeTab === 'pending' ? 'submitted' : activeTab;
      const response = await api.get(`/timesheets/admin/grouped${statusParam ? `?status=${statusParam}` : ''}`);
      setGroupedData(response.data);
      
      // Calculate stats from all groups
      let pending = 0, approved = 0, rejected = 0, totalHours = 0;
      response.data.groups.forEach(group => {
        pending += group.pending_count || 0;
        approved += group.approved_count || 0;
        rejected += group.rejected_count || 0;
        totalHours += group.total_hours || 0;
      });
      
      setStats({ pending, approved, rejected, totalHours });
    } catch (error) {
      toast.error('Failed to load timesheets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [api, activeTab]);

  const fetchOperators = useCallback(async () => {
    try {
      const response = await api.get('/operators');
      setOperators(response.data);
    } catch (error) {
      console.error('Failed to load operators');
    }
  }, [api]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  // Re-fetch when timesheets feature becomes available (tier data loads)
  useEffect(() => {
    if (hasFeature('timesheets')) {
      fetchTimesheets();
    }
  }, [hasFeature]);

  useEffect(() => {
    const loadAllowanceTypes = async () => {
      try {
        const res = await api.get('/allowance-types');
        setAllowanceTypes((res.data || []).filter(t => t.is_active));
      } catch { /* ignore */ }
    };
    loadAllowanceTypes();
  }, [api]);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  // One-time tenant time format fetch
  useEffect(() => {
    api.get(`/tenants/${user.client_id}`).then(res => {
      if (res.data?.time_format) setTimeFormat(res.data.time_format);
    }).catch(() => {});
  }, [api, user.client_id]);

  // Accounting connection status (for the Payroll Export tab)
  useEffect(() => {
    if (!hasFeatureRef.current('payroll_export')) return;
    Promise.allSettled([api.get('/xero/status'), api.get('/myob/status')]).then(([x, m]) => {
      setAcctStatus({
        xero: x.status === 'fulfilled' && !!x.value.data?.connected,
        myob: m.status === 'fulfilled' && !!m.value.data?.connected,
      });
    });
    api.get('/accounting/connections')
      .then(res => setPayrollConns(res.data.connections || []))
      .catch(() => {});
  }, [api]);

  const handleApprove = async (timesheetId) => {
    try {
      await api.post(`/timesheets/${timesheetId}/approval`, { action: 'approve' });
      toast.success('Timesheet approved');
      fetchTimesheets();
      // Update selected group if open
      if (selectedGroup) {
        const updatedTimesheets = selectedGroup.timesheets.map(ts => 
          ts.id === timesheetId ? { ...ts, status: 'approved' } : ts
        );
        setSelectedGroup({ ...selectedGroup, timesheets: updatedTimesheets });
      }
      setSelectedTimesheets(prev => prev.filter(id => id !== timesheetId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`/timesheets/${selectedTimesheet.id}/approval`, { 
        action: 'reject',
        reason: rejectionReason 
      });
      toast.success('Timesheet rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject timesheet');
    }
  };

  const handleRevertToPending = async (timesheetId) => {
    try {
      await api.post(`/timesheets/${timesheetId}/revert-to-pending`);
      toast.success('Timesheet reverted to pending');
      if (selectedGroup) {
        setSelectedGroup({
          ...selectedGroup,
          timesheets: selectedGroup.timesheets.map(ts =>
            ts.id === timesheetId ? { ...ts, status: 'submitted', rejection_reason: null } : ts
          ),
        });
      }
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revert timesheet');
    }
  };

  const openDeleteDialog = (ts) => {
    setTimesheetToDelete(ts);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!timesheetToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/timesheets/${timesheetToDelete.id}`);
      toast.success('Timesheet deleted');
      setDeleteDialogOpen(false);
      // Optimistically remove from the open group drawer
      if (selectedGroup) {
        setSelectedGroup({
          ...selectedGroup,
          timesheets: selectedGroup.timesheets.filter(t => t.id !== timesheetToDelete.id),
        });
      }
      setSelectedTimesheets(prev => prev.filter(id => id !== timesheetToDelete.id));
      setTimesheetToDelete(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete timesheet');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkApprove = async () => {
    const approvableIds = (selectedGroup?.timesheets || [])
      .filter(t => selectedTimesheets.includes(t.id) && t.status === 'submitted')
      .map(t => t.id);
    if (approvableIds.length === 0) {
      toast.error('No pending timesheets selected');
      return;
    }
    try {
      const response = await api.post('/timesheets/admin/bulk-approve', approvableIds);
      toast.success(`${response.data.approved_count} timesheets approved`);
      setSelectedTimesheets([]);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheets');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTimesheets.length === 0) return;
    setBulkDeleting(true);
    try {
      const response = await api.post('/timesheets/admin/bulk-delete', selectedTimesheets);
      toast.success(`${response.data.deleted_count} timesheet(s) deleted`);
      setBulkDeleteDialogOpen(false);
      if (selectedGroup) {
        setSelectedGroup({
          ...selectedGroup,
          timesheets: selectedGroup.timesheets.filter(t => !selectedTimesheets.includes(t.id)),
        });
      }
      setSelectedTimesheets([]);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete timesheets');
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectTimesheet = (id) => {
    setSelectedTimesheets(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAllInGroup = () => {
    if (!selectedGroup) return;
    const pendingIds = selectedGroup.timesheets.filter(t => t.status === 'submitted').map(t => t.id);
    if (selectedTimesheets.length === pendingIds.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(pendingIds);
    }
  };

  // Docket cross-reference
  const loadDocketCrossRef = async (timesheetId) => {
    setDocketCrossRefLoading(true);
    try {
      const res = await api.get(`/timesheets/${timesheetId}/docket-times`);
      setDocketCrossRef(res.data);
    } catch {
      toast.error('Failed to load docket times');
      setDocketCrossRef(null);
    } finally {
      setDocketCrossRefLoading(false);
    }
  };

  /** Return 'ok' | 'amber' | 'red' for a time mismatch in minutes */
  const getMismatchLevel = (tsMinutes, docketMinutes) => {
    const diff = Math.abs(tsMinutes - docketMinutes);
    if (diff >= 30) return 'red';
    if (diff >= 15) return 'amber';
    return 'ok';
  };

  /** Convert "HH:MM" string to total minutes since midnight */
  const timeToMinutes = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return formatTimeUtil(timeString, timeFormat);
    }
    try {
      return formatTimeUtil(timeString, timeFormat);
    } catch {
      return timeString;
    }
  };

  // Permission checks
  const canWrite = user?.roles?.includes('admin') || user?.roles?.includes('payroll') || user?.roles?.includes('dispatcher');
  const canApprove = user?.roles?.includes('admin') || user?.roles?.includes('payroll') || user?.roles?.includes('dispatcher');

  // Edit timesheet state
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editForm, setEditForm] = useState({ start_time: '', end_time: '', break_minutes: 0, notes: '', edit_reason: '', allowances: [], time_blocks: [] });
  const [allowanceTypes, setAllowanceTypes] = useState([]);

  // Create timesheet state
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ operator_id: '', job_id: '', date: '', start_time: '07:00', end_time: '15:00', break_minutes: 30, notes: '', time_blocks: [] });
  const [availableJobs, setAvailableJobs] = useState([]);

  const handleOpenEdit = (ts) => {
    setEditingTimesheet(ts);
    setEditForm({
      start_time: ts.start_time || '',
      end_time: ts.end_time || ts.finish_time || '',
      break_minutes: ts.break_minutes ?? ts.lunch_break_minutes ?? 0,
      notes: ts.notes || '',
      edit_reason: '',
      allowances: ts.allowances || [],
      time_blocks: ts.time_blocks || [],
    });
    setEditDrawerOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.edit_reason.trim()) { toast.error('Edit reason is required'); return; }
    const payload = { ...editForm };
    if (payload.time_blocks && payload.time_blocks.length > 0) {
      // When blocks exist, recalculate total from blocks
      payload.time_blocks = payload.time_blocks.map(b => ({
        ...b,
        hours: parseFloat(calcBlockHoursUtil(b.start_time, b.end_time, b.break_minutes || 0).toFixed(2)),
      }));
    } else if (!editingTimesheet?.time_blocks?.length) {
      // Timesheet has no blocks and user didn't add any — don't send empty array
      delete payload.time_blocks;
    }
    try {
      const res = await api.put(`/timesheets/${editingTimesheet.id}/admin-edit`, payload);
      const warnings = res.data?.block_warnings;
      if (warnings?.length) {
        toast.warning(`Saved with ${warnings.length} overlap warning(s) — review time blocks`);
      } else {
        toast.success('Timesheet updated');
      }
      setEditDrawerOpen(false);
      setEditingTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timesheet');
    }
  };

  // Auto-fill blocks from dockets
  const handleAutoFillBlocks = async (operatorId, date, context = 'edit') => {
    try {
      const params = new URLSearchParams({ operator_id: operatorId, date });
      const startTime = context === 'edit' ? editForm.start_time : createForm.start_time;
      const endTime = context === 'edit' ? (editForm.end_time || editForm.finish_time) : createForm.end_time;
      if (startTime) params.append('shift_start', startTime);
      if (endTime) params.append('shift_end', endTime);
      const res = await api.get(`/timesheets/auto-blocks?${params}`);
      const newBlocks = res.data.blocks || [];
      if (newBlocks.length === 0) {
        toast.info('No dockets found for this date to generate blocks from');
        return;
      }
      if (context === 'edit') {
        setEditForm(prev => ({ ...prev, time_blocks: newBlocks }));
      } else {
        setCreateForm(prev => ({ ...prev, time_blocks: newBlocks }));
      }
      toast.success(`Generated ${newBlocks.length} block(s) from ${res.data.docket_count} docket(s)`);
    } catch (err) {
      toast.error('Failed to auto-fill blocks from dockets');
    }
  };

  const handleOpenCreate = () => {
    setCreateForm({ operator_id: '', job_id: '', date: format(new Date(), 'yyyy-MM-dd'), start_time: '07:00', end_time: '15:00', break_minutes: 30, notes: '', time_blocks: [] });
    setAvailableJobs([]);
    setCreateDrawerOpen(true);
  };

  const handleCreateOperatorChange = async (operatorId) => {
    setCreateForm(prev => ({ ...prev, operator_id: operatorId, job_id: '' }));
    if (operatorId) {
      try {
        const response = await api.get('/jobs');
        const allJobs = response.data || [];
        // Filter jobs that have this operator allocated
        const operatorJobs = allJobs.filter(j => 
          j.status !== 'cancelled' && (j.start_date <= createForm.date || !createForm.date)
        );
        setAvailableJobs(operatorJobs);
      } catch { setAvailableJobs([]); }
    }
  };

  const handleSaveCreate = async () => {
    if (!createForm.operator_id) { toast.error('Select a worker'); return; }
    if (!createForm.date) { toast.error('Date is required'); return; }
    if (!createForm.start_time || !createForm.end_time) { toast.error('Start and end time are required'); return; }
    const payload = { ...createForm };
    if (payload.time_blocks && payload.time_blocks.length > 0) {
      payload.time_blocks = payload.time_blocks.map(b => ({
        ...b,
        hours: parseFloat(calcBlockHoursUtil(b.start_time, b.end_time, b.break_minutes || 0).toFixed(2)),
      }));
    } else {
      delete payload.time_blocks;
    }
    try {
      const res = await api.post('/timesheets/admin/create', payload);
      const warnings = res.data?.block_warnings;
      if (warnings?.length) {
        toast.warning(`Created with ${warnings.length} overlap warning(s) — review time blocks`);
      } else {
        toast.success('Timesheet created');
      }
      setCreateDrawerOpen(false);
      fetchTimesheets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd MMM');
    } catch {
      return dateString;
    }
  };

  const formatPeriodLabel = (group) => {
    const start = formatDateShort(group.period_start);
    const end = formatDateShort(group.period_end);
    return `${start} - ${end}`;
  };

  const getPayCycleDescription = () => {
    const { pay_cycle_type, pay_cycle_day } = groupedData;
    if (pay_cycle_type === 'monthly') {
      return `${PAY_CYCLE_LABELS[pay_cycle_type]} (${pay_cycle_day}${getOrdinalSuffix(pay_cycle_day)} of month)`;
    }
    return `${PAY_CYCLE_LABELS[pay_cycle_type]} (${DAY_NAMES[pay_cycle_day]})`;
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const openGroupDrawer = (group) => {
    setSelectedGroup(group);
    setSelectedTimesheets([]);
    setExpandedOperator(null);
    setGroupDrawerOpen(true);
  };

  // Group timesheets by operator within a pay cycle
  const getOperatorGroups = (timesheets) => {
    const groups = {};
    timesheets.forEach(ts => {
      const opId = ts.operator_id || 'unknown';
      if (!groups[opId]) {
        groups[opId] = {
          operator_id: opId,
          operator_name: ts.operator_name || 'Unknown',
          timesheets: [],
          total_hours: 0,
          pending_count: 0,
          approved_count: 0,
          rejected_count: 0,
          exported_count: 0,
        };
      }
      groups[opId].timesheets.push(ts);
      groups[opId].total_hours += ts.total_hours || 0;
      if (ts.status === 'submitted') groups[opId].pending_count++;
      else if (ts.status === 'approved') groups[opId].approved_count++;
      else if (ts.status === 'rejected') groups[opId].rejected_count++;
      if (ts.export_status === 'exported') groups[opId].exported_count++;
    });
    return Object.values(groups).sort((a, b) => a.operator_name.localeCompare(b.operator_name));
  };

  // Filter groups by search/operator
  const filteredGroups = groupedData.groups.filter(group => {
    if (!searchTerm && operatorFilter === 'all') return true;
    
    // Check if any timesheet in group matches filters
    return group.timesheets.some(ts => {
      const matchesSearch = !searchTerm || 
        ts.operator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ts.job_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ts.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOperator = operatorFilter === 'all' || ts.operator_id === operatorFilter;
      return matchesSearch && matchesOperator;
    });
  });

  // Filter timesheets within selected group
  const filteredGroupTimesheets = selectedGroup?.timesheets.filter(ts => {
    const matchesSearch = !searchTerm || 
      ts.operator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ts.job_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ts.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesOperator = operatorFilter === 'all' || ts.operator_id === operatorFilter;
    return matchesSearch && matchesOperator;
  }) || [];

  return (
    <FeatureGate 
      feature="timesheets"
      title="Timesheets"
      description="Review and approve operator timesheets submitted from the mobile app. Track hours worked, manage approvals, and maintain accurate payroll records."
    >
      <div className="space-y-6" data-testid="timesheets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Timesheets</h1>
            <p className="text-muted-foreground">
              Review and approve operator time entries • {getPayCycleDescription()}
            </p>
          </div>
          {canWrite && (
            <Button className="btn-volt" onClick={handleOpenCreate} data-testid="add-timesheet-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Timesheet
            </Button>
          )}
        </div>

        {/* Top-level tabs: Timesheets vs Leave Requests */}
        {hasFeature('worker_availability') && (
          <div className="flex gap-2 border-b border-border pb-2">
            <Button
              variant={topTab === 'timesheets' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTopTab('timesheets')}
              data-testid="top-tab-timesheets"
            >
              <Clock className="h-4 w-4 mr-2" /> Timesheets
            </Button>
            <Button
              variant={topTab === 'leave-requests' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTopTab('leave-requests')}
              data-testid="top-tab-leave-requests"
            >
              <Calendar className="h-4 w-4 mr-2" /> Leave Requests
            </Button>
          </div>
        )}

        {topTab === 'leave-requests' && hasFeature('worker_availability') ? (
          <LeaveRequestsTab api={api} formatDateDisplay={formatDate} />
        ) : (
        <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-industrial cursor-pointer hover:ring-2 hover:ring-orange-500/50 transition-all" onClick={() => setActiveTab('pending')}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending Approval</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial cursor-pointer hover:ring-2 hover:ring-green-500/50 transition-all" onClick={() => setActiveTab('approved')}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.approved}</p>
                  <p className="text-xs text-muted-foreground">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial cursor-pointer hover:ring-2 hover:ring-red-500/50 transition-all" onClick={() => setActiveTab('rejected')}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.rejected}</p>
                  <p className="text-xs text-muted-foreground">Rejected</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-industrial">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                  <Clock className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalHours.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Pay Cycles</TabsTrigger>
            <TabsTrigger value="pending">
              Pending
              {stats.pending > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {stats.pending}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected">Rejected</TabsTrigger>
            {hasFeature('payroll_export') && canAccessPayroll && (
              <TabsTrigger value="payroll-export" data-testid="tab-payroll-export">Payroll Export</TabsTrigger>
            )}
          </TabsList>

          {/* Filters */}
          {activeTab !== 'payroll-export' && (
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by operator, job, or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={operatorFilter} onValueChange={setOperatorFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by worker" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workers</SelectItem>
                {operators.map(op => (
                  <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}

          {/* Content - Pay Cycle Groups */}
          <TabsContent value={activeTab === 'payroll-export' ? '__timesheets_list__' : activeTab} className="mt-4">
            <Card className="card-industrial">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-pulse text-muted-foreground">Loading timesheets...</div>
                  </div>
                ) : filteredGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CalendarDays className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="font-semibold text-lg">No Pay Cycles Found</h3>
                    <p className="text-muted-foreground text-sm">
                      No timesheet entries match your filters
                    </p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredGroups.map((group, idx) => (
                      <div 
                        key={`${group.period_start}_${group.period_end}`}
                        className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => openGroupDrawer(group)}
                        data-testid={`pay-cycle-group-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-[#ffd108]/10 rounded-lg">
                              <CalendarDays className="h-6 w-6 text-[#ffd108]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{formatPeriodLabel(group)}</h3>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  {group.operator_count} operator{group.operator_count !== 1 ? 's' : ''}
                                </span>
                                <span>•</span>
                                <span>{group.total_entries} entries</span>
                                <span>•</span>
                                <span className="font-mono font-semibold text-foreground">
                                  {group.total_hours.toFixed(1)}h
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {group.pending_count > 0 && (
                              <Badge className="bg-orange-500 hover:bg-[#CF5F12] text-white">
                                {group.pending_count} pending
                              </Badge>
                            )}
                            {group.approved_count > 0 && (
                              <Badge className="bg-green-500 hover:bg-[#1C9E3E] text-white">
                                {group.approved_count} approved
                              </Badge>
                            )}
                            {group.rejected_count > 0 && (
                              <Badge className="bg-red-500 hover:bg-red-600 text-white">
                                {group.rejected_count} rejected
                              </Badge>
                            )}
                            {group.exported_count > 0 && (
                              <Badge variant="outline" className="border-indigo-500/50 text-indigo-600">
                                <Upload className="h-3 w-3 mr-1" /> {group.exported_count} exported
                              </Badge>
                            )}
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {hasFeature('payroll_export') && canAccessPayroll && (
            <TabsContent value="payroll-export" className="mt-4">
              <div className="grid gap-6" data-testid="timesheets-payroll-export">
                {!acctStatus.xero && !acctStatus.myob && (payrollConns || []).filter(c => c.status !== 'disconnected').length === 0 && (
                  <Card className="card-industrial">
                    <CardContent className="py-8 text-center text-sm text-muted-foreground">
                      Connect Xero or MYOB in <span className="font-medium">Settings → Accounting</span> to export timesheets. Configure employee and rate mapping there first (Accounting → Payroll).
                    </CardContent>
                  </Card>
                )}
                {['xero', 'myob'].map((prov) => {
                  const provName = prov === 'xero' ? 'Xero' : 'MYOB';
                  const connected = prov === 'xero' ? acctStatus.xero : acctStatus.myob;
                  const conns = (payrollConns || []).filter(c => c.provider === prov && c.status !== 'disconnected');
                  if (conns.length === 0) {
                    if (!connected) return null;
                    return <PayrollExport key={prov} api={api} platform={prov} platformName={provName} />;
                  }
                  if (conns.length === 1) {
                    return <PayrollExport key={prov} api={api} platform={prov} platformName={provName} connectionId={conns[0].id} />;
                  }
                  return conns.map((c) => {
                    const cLabel = c.label || c.company_name || provName;
                    return (
                      <PayrollExport key={c.id} api={api} platform={prov} platformName={`${provName} — ${cLabel}`} connectionId={c.id} />
                    );
                  });
                })}
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Pay Cycle Group Drawer */}
        <Sheet open={groupDrawerOpen} onOpenChange={setGroupDrawerOpen}>
          <SheetContent side="right" className="w-full max-w-[1000px] sm:w-[1000px] overflow-y-auto">
            <SheetHeader className="pb-4 border-b">
              <SheetTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-[#ffd108]" />
                {selectedGroup && formatPeriodLabel(selectedGroup)}
              </SheetTitle>
              {selectedGroup && (
                <p className="text-sm text-muted-foreground">
                  {selectedGroup.operator_count} operators • {selectedGroup.total_entries} entries • {selectedGroup.total_hours.toFixed(1)} total hours
                </p>
              )}
            </SheetHeader>
            
            {selectedGroup && (
              <div className="py-4">
                {/* Bulk actions */}
                {selectedTimesheets.length > 0 && (
                  <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-sm" data-testid="ts-bulk-selected-count">{selectedTimesheets.length} timesheet(s) selected</span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="btn-volt" onClick={handleBulkApprove} data-testid="ts-bulk-approve-btn">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Approve Selected
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)} data-testid="ts-bulk-delete-btn">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected ({selectedTimesheets.length})
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Operator Groups */}
                <div className="space-y-2">
                  {getOperatorGroups(filteredGroupTimesheets).map((opGroup) => (
                    <div key={opGroup.operator_id} className="border rounded-lg overflow-hidden">
                      {/* Operator Header Row */}
                      <div 
                        className="p-4 bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors flex items-center justify-between"
                        onClick={() => setExpandedOperator(expandedOperator === opGroup.operator_id ? null : opGroup.operator_id)}
                        data-testid={`operator-group-${opGroup.operator_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-500/10 rounded-full">
                            <User className="h-5 w-5 text-blue-500" />
                          </div>
                          <div>
                            <h4 className="font-semibold">{opGroup.operator_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {opGroup.timesheets.length} entries • <span className="font-mono font-medium text-foreground">{opGroup.total_hours.toFixed(1)}h</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {opGroup.pending_count > 0 && (
                            <Badge className="bg-orange-500 hover:bg-[#CF5F12] text-white text-xs">
                              {opGroup.pending_count} pending
                            </Badge>
                          )}
                          {opGroup.approved_count > 0 && (
                            <Badge className="bg-green-500 hover:bg-[#1C9E3E] text-white text-xs">
                              {opGroup.approved_count} approved
                            </Badge>
                          )}
                          <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${expandedOperator === opGroup.operator_id ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Expanded Timesheet Entries */}
                      {expandedOperator === opGroup.operator_id && (
                        <div className="border-t">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/20">
                                <TableHead className="w-[50px]">
                                  <Checkbox 
                                    checked={opGroup.timesheets.length > 0 && opGroup.timesheets.every(t => selectedTimesheets.includes(t.id))}
                                    onCheckedChange={(checked) => {
                                      const allIds = opGroup.timesheets.map(t => t.id);
                                      if (checked) {
                                        setSelectedTimesheets(prev => [...new Set([...prev, ...allIds])]);
                                      } else {
                                        setSelectedTimesheets(prev => prev.filter(id => !allIds.includes(id)));
                                      }
                                    }}
                                    data-testid={`select-all-ts-${opGroup.operator_id}`}
                                  />
                                </TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Job / Customer</TableHead>
                                <TableHead>Start</TableHead>
                                <TableHead>End</TableHead>
                                <TableHead>Hours</TableHead>
                                <TableHead>Notes</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {opGroup.timesheets.map((ts) => {
                                const StatusIcon = STATUS_CONFIG[ts.status]?.icon || Clock;
                                return (
                                  <TableRow key={ts.id}>
                                    <TableCell>
                                      <Checkbox 
                                        checked={selectedTimesheets.includes(ts.id)}
                                        onCheckedChange={() => toggleSelectTimesheet(ts.id)}
                                        data-testid={`select-ts-${ts.id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatDate(ts.date)}
                                    </TableCell>
                                    <TableCell>
                                      <div className="space-y-0.5">
                                        {ts.job_description ? (
                                          <>
                                            <p className="text-sm font-medium truncate max-w-[200px]">{ts.job_description}</p>
                                            <p className="text-xs text-muted-foreground">{ts.customer_name} • {ts.site_name}</p>
                                          </>
                                        ) : ts.allocated_jobs?.length > 0 ? (
                                          ts.allocated_jobs.map((j, i) => (
                                            <p key={i} className="text-sm">
                                              <span className="font-medium">#{j.job_number}</span>
                                              {j.customer_name ? ` — ${j.customer_name}` : ''}
                                            </p>
                                          ))
                                        ) : (
                                          <p className="text-xs text-muted-foreground">No allocations</p>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatTime(ts.start_time)}
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">
                                      {formatTime(ts.finish_time || ts.end_time)}
                                    </TableCell>
                                    <TableCell>
                                      <span className="font-mono font-semibold">
                                        {ts.total_hours ? `${ts.total_hours.toFixed(1)}h` : '-'}
                                      </span>
                                      {ts.allowances?.length > 0 && (
                                        <span className="block text-[10px] text-amber-500 mt-0.5">+{ts.allowances.length} allowance{ts.allowances.length > 1 ? 's' : ''}</span>
                                      )}
                                      {ts.time_blocks?.length > 0 && (
                                        <span className="block text-[10px] text-blue-500 mt-0.5">{ts.time_blocks.length} block{ts.time_blocks.length > 1 ? 's' : ''}</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                                        {ts.notes || '-'}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex flex-col gap-1">
                                        <Badge className={`${STATUS_CONFIG[ts.status]?.color} text-white`}>
                                          <StatusIcon className="h-3 w-3 mr-1" />
                                          {STATUS_CONFIG[ts.status]?.label}
                                        </Badge>
                                        {ts.export_status === 'exported' && (
                                          <Badge variant="outline" className="border-indigo-500/50 text-indigo-600 text-[10px] w-fit" data-testid={`exported-badge-${ts.id}`}>
                                            <Upload className="h-2.5 w-2.5 mr-1" /> Exported
                                          </Badge>
                                        )}
                                        {ts.export_status === 'failed' && (
                                          <Badge variant="outline" className="border-red-500/50 text-red-600 text-[10px] w-fit" data-testid={`export-failed-badge-${ts.id}`}>
                                            <AlertCircle className="h-2.5 w-2.5 mr-1" /> Export Failed
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center justify-end gap-1">
                                        {ts.status === 'submitted' && (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                              onClick={(e) => { e.stopPropagation(); handleApprove(ts.id); }}
                                              data-testid={`approve-ts-${ts.id}`}
                                            >
                                              <Check className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedTimesheet(ts);
                                                setRejectDialogOpen(true);
                                              }}
                                              data-testid={`reject-ts-${ts.id}`}
                                            >
                                              <XIcon className="h-4 w-4" />
                                            </Button>
                                          </>
                                        )}
                                        {(ts.status === 'rejected' || ts.status === 'approved') && canWrite && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                            onClick={(e) => { e.stopPropagation(); handleRevertToPending(ts.id); }}
                                            title="Revert to Pending"
                                            data-testid={`revert-ts-${ts.id}`}
                                          >
                                            <RotateCcw className="h-4 w-4" />
                                          </Button>
                                        )}
                                        {canWrite && (ts.status === 'submitted' || ts.status === 'rejected') && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(ts); }}
                                            data-testid={`edit-ts-${ts.id}`}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                        )}
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-8 w-8 p-0"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTimesheet(ts);
                                            setDocketCrossRef(null);
                                            setViewDialogOpen(true);
                                          }}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        {canWrite && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={(e) => { e.stopPropagation(); openDeleteDialog(ts); }}
                                            data-testid={`delete-ts-${ts.id}`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <SheetFooter className="pt-4 border-t mt-6">
              <Button variant="outline" onClick={() => setGroupDrawerOpen(false)}>Close</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* View Timesheet Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Timesheet Details</DialogTitle>
            </DialogHeader>
            {selectedTimesheet && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Worker</Label>
                    <p className="font-medium">{selectedTimesheet.operator_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <p className="font-medium">{formatDate(selectedTimesheet.date)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Time</Label>
                    <p className="font-mono">{formatTime(selectedTimesheet.start_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">End Time</Label>
                    <p className="font-mono">{formatTime(selectedTimesheet.finish_time || selectedTimesheet.end_time)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Break</Label>
                    <p className="font-mono">{selectedTimesheet.lunch_break_minutes || selectedTimesheet.break_minutes || 0} mins</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Hours</Label>
                    <p className="font-mono font-semibold text-lg">
                      {selectedTimesheet.total_hours ? `${selectedTimesheet.total_hours.toFixed(2)}h` : '-'}
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Job</Label>
                  {selectedTimesheet.job_description ? (
                    <>
                      <p className="font-medium">{selectedTimesheet.job_description}</p>
                      <p className="text-sm text-muted-foreground">{selectedTimesheet.customer_name} • {selectedTimesheet.site_name}</p>
                    </>
                  ) : selectedTimesheet.allocated_jobs?.length > 0 ? (
                    selectedTimesheet.allocated_jobs.map((j, i) => (
                      <p key={i} className="text-sm">
                        <span className="font-medium">#{j.job_number}</span>
                        {j.customer_name ? ` — ${j.customer_name}` : ''}
                      </p>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No allocations</p>
                  )}
                </div>
                {selectedTimesheet.notes && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Notes</Label>
                    <p className="text-sm">{selectedTimesheet.notes}</p>
                  </div>
                )}

                {/* Time Blocks (multi-block entries) */}
                {selectedTimesheet.time_blocks?.length > 0 && (
                  <div className="border-t pt-4">
                    <TimeBlockReadOnly
                      blocks={selectedTimesheet.time_blocks}
                      connectedPlatform={payrollCtx.platform}
                      trackingCategories={payrollCtx.trackingCategories}
                      myobJobs={payrollCtx.myobJobs}
                      timesheetCategoryId={payrollCtx.timesheetCategoryId}
                    />
                  </div>
                )}
                {selectedTimesheet.status === 'rejected' && selectedTimesheet.rejection_reason && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Label className="text-xs text-red-600">Rejection Reason</Label>
                    <p className="text-sm text-red-800">{selectedTimesheet.rejection_reason}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${STATUS_CONFIG[selectedTimesheet.status]?.color} text-white`}>
                      {STATUS_CONFIG[selectedTimesheet.status]?.label}
                    </Badge>
                    {selectedTimesheet.export_status === 'exported' && (
                      <Badge variant="outline" className="border-indigo-500/50 text-indigo-600 text-xs">
                        <Upload className="h-3 w-3 mr-1" /> Exported{selectedTimesheet.export_platform ? ` to ${selectedTimesheet.export_platform === 'xero' ? 'Xero' : 'MYOB'}` : ''}
                      </Badge>
                    )}
                    {selectedTimesheet.export_status === 'failed' && (
                      <Badge variant="outline" className="border-red-500/50 text-red-600 text-xs">
                        <AlertCircle className="h-3 w-3 mr-1" /> Export Failed
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Docket Cross-Reference */}
                <div className="border-t pt-4 mt-2">
                  {!docketCrossRef && !docketCrossRefLoading && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      data-testid="load-docket-times-btn"
                      onClick={() => loadDocketCrossRef(selectedTimesheet.id)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Load Docket Times
                    </Button>
                  )}
                  {docketCrossRefLoading && (
                    <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading docket times...
                    </div>
                  )}
                  {docketCrossRef && (
                    <div className="space-y-3" data-testid="docket-cross-ref-section">
                      <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                        Docket Cross-Reference
                      </Label>
                      {docketCrossRef.dockets?.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No dockets found for this date.</p>
                      ) : (
                        <>
                          {/* Aggregated comparison — earliest docket start vs TS start, latest docket end vs TS end */}
                          {(() => {
                            const allStarts = docketCrossRef.dockets.map(d => timeToMinutes(d.earliest_start)).filter(v => v != null);
                            const allEnds = docketCrossRef.dockets.map(d => timeToMinutes(d.latest_finish)).filter(v => v != null);
                            const aggStartMin = allStarts.length ? Math.min(...allStarts) : null;
                            const aggEndMin = allEnds.length ? Math.max(...allEnds) : null;
                            const aggTotalHours = docketCrossRef.dockets.reduce((s, d) => s + (d.total_hours || 0), 0);
                            const tsStart = timeToMinutes(selectedTimesheet.start_time);
                            const tsEnd = timeToMinutes(selectedTimesheet.finish_time || selectedTimesheet.end_time);
                            const startLevel = (tsStart != null && aggStartMin != null) ? getMismatchLevel(tsStart, aggStartMin) : 'ok';
                            const endLevel = (tsEnd != null && aggEndMin != null) ? getMismatchLevel(tsEnd, aggEndMin) : 'ok';
                            const tsTotalMins = (selectedTimesheet.total_hours || 0) * 60;
                            const aggTotalMins = aggTotalHours * 60;
                            const totalLevel = getMismatchLevel(tsTotalMins, aggTotalMins);
                            const mismatchColor = (level) =>
                              level === 'red' ? 'text-red-400 font-semibold' :
                              level === 'amber' ? 'text-amber-400 font-semibold' : '';
                            const minsToTimeStr = (m) => {
                              if (m == null) return null;
                              const h = Math.floor(m / 60);
                              const min = m % 60;
                              return `${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
                            };
                            return (
                              <div className={`rounded-md border p-3 text-sm ${
                                [startLevel, endLevel, totalLevel].includes('red') ? 'bg-red-500/10 border-red-500/30' :
                                [startLevel, endLevel, totalLevel].includes('amber') ? 'bg-amber-500/10 border-amber-500/30' : 'bg-muted/20'
                              }`}>
                                <div className="grid grid-cols-4 gap-1 text-xs text-muted-foreground font-medium mb-1 px-1">
                                  <span></span>
                                  <span>Start</span>
                                  <span>End</span>
                                  <span>Total</span>
                                </div>
                                <div className="grid grid-cols-4 gap-1 items-center px-1 py-0.5">
                                  <span className="text-xs font-medium">Timesheet</span>
                                  <span className="font-mono text-sm">{formatTime(selectedTimesheet.start_time)}</span>
                                  <span className="font-mono text-sm">{formatTime(selectedTimesheet.finish_time || selectedTimesheet.end_time)}</span>
                                  <span className="font-mono text-sm">{selectedTimesheet.total_hours ? `${selectedTimesheet.total_hours.toFixed(2)}h` : '—'}</span>
                                </div>
                                <div className="grid grid-cols-4 gap-1 items-center px-1 py-0.5">
                                  <span className="text-xs font-medium">Dockets</span>
                                  <span className={`font-mono text-sm ${mismatchColor(startLevel)}`}>{aggStartMin != null ? formatTime(minsToTimeStr(aggStartMin)) : '—'}</span>
                                  <span className={`font-mono text-sm ${mismatchColor(endLevel)}`}>{aggEndMin != null ? formatTime(minsToTimeStr(aggEndMin)) : '—'}</span>
                                  <span className={`font-mono text-sm ${mismatchColor(totalLevel)}`}>{aggTotalHours ? `${aggTotalHours.toFixed(2)}h` : '—'}</span>
                                </div>

                                {[startLevel, endLevel, totalLevel].includes('red') && (
                                  <div className="flex items-center gap-1.5 text-xs text-red-400 mt-2">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    <span>Significant time discrepancy detected (30+ min difference)</span>
                                  </div>
                                )}
                                {![startLevel, endLevel, totalLevel].includes('red') && [startLevel, endLevel, totalLevel].includes('amber') && (
                                  <div className="flex items-center gap-1.5 text-xs text-amber-400 mt-2">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    <span>Minor time discrepancy detected (15–30 min difference)</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Individual docket details */}
                          {docketCrossRef.dockets.map((d) => (
                            <div key={d.docket_id} className="border rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-medium text-sm">
                                    Docket #{d.docket_number || '—'}
                                  </span>
                                  {d.job_number && (
                                    <span className="text-xs text-muted-foreground ml-2">
                                      Job #{d.job_number}
                                    </span>
                                  )}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {d.status}
                                </Badge>
                              </div>
                              {d.customer_name && (
                                <p className="text-xs text-muted-foreground">{d.customer_name}{d.equipment_type ? ` — ${d.equipment_type}` : ''}</p>
                              )}
                              <div className="flex gap-4 text-xs text-muted-foreground">
                                <span>Start: <span className="font-mono">{d.earliest_start ? formatTime(d.earliest_start) : '—'}</span></span>
                                <span>End: <span className="font-mono">{d.latest_finish ? formatTime(d.latest_finish) : '—'}</span></span>
                                <span>Total: <span className="font-mono">{d.total_hours ? `${d.total_hours.toFixed(2)}h` : '—'}</span></span>
                              </div>
                              {d.timed_items?.length > 0 && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                    {d.timed_items.length} line item{d.timed_items.length !== 1 ? 's' : ''}
                                  </summary>
                                  <div className="mt-1 space-y-0.5 pl-2 border-l-2 border-muted">
                                    {d.timed_items.map((item, idx) => (
                                      <div key={idx} className="flex justify-between">
                                        <span>{item.type_name || 'Item'}</span>
                                        <span className="font-mono">
                                          {item.start_time || '—'}–{item.finish_time || '—'}
                                          {item.qty != null ? ` (${item.qty}h)` : ''}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
              {selectedTimesheet?.status === 'submitted' && (
                <>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      setViewDialogOpen(false);
                      setRejectDialogOpen(true);
                    }}
                  >
                    Reject
                  </Button>
                  <Button 
                    className="btn-volt"
                    onClick={() => {
                      handleApprove(selectedTimesheet.id);
                      setViewDialogOpen(false);
                    }}
                  >
                    Approve
                  </Button>
                </>
              )}
              {(selectedTimesheet?.status === 'rejected' || selectedTimesheet?.status === 'approved') && canWrite && (
                <Button
                  variant="outline"
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  data-testid="revert-to-pending-btn"
                  onClick={() => {
                    handleRevertToPending(selectedTimesheet.id);
                    setViewDialogOpen(false);
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Revert to Pending
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Timesheet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Please provide a reason for rejecting this timesheet. The operator will be notified.
              </p>
              <div className="space-y-2">
                <Label>Rejection Reason</Label>
                <Textarea
                  placeholder="e.g., Hours do not match job requirements, incorrect break time..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason('');
              }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleReject}>
                Reject Timesheet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="delete-timesheet-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Delete Timesheet
              </DialogTitle>
              <DialogDescription>
                This will permanently remove this time entry. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {timesheetToDelete && (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Worker:</span> <span className="font-medium">{timesheetToDelete.operator_name}</span></p>
                <p><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDate(timesheetToDelete.date)}</span></p>
                <p>
                  <span className="text-muted-foreground">Hours:</span>{' '}
                  <span className="font-mono font-medium">{timesheetToDelete.total_hours ? `${timesheetToDelete.total_hours.toFixed(1)}h` : '-'}</span>
                  {'  '}<span className="text-muted-foreground">Status:</span>{' '}
                  <span className="font-medium">{STATUS_CONFIG[timesheetToDelete.status]?.label || timesheetToDelete.status}</span>
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="delete-ts-cancel-btn">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting} data-testid="delete-ts-confirm-btn">
                {deleting ? 'Deleting…' : 'Delete Timesheet'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="bulk-delete-timesheet-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Delete {selectedTimesheets.length} Timesheet{selectedTimesheets.length !== 1 ? 's' : ''}
              </DialogTitle>
              <DialogDescription>
                This will permanently remove {selectedTimesheets.length} selected time entr{selectedTimesheets.length !== 1 ? 'ies' : 'y'}. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} data-testid="bulk-delete-ts-cancel-btn">
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="bulk-delete-ts-confirm-btn">
                {bulkDeleting ? 'Deleting…' : `Delete ${selectedTimesheets.length} Timesheet${selectedTimesheets.length !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Timesheet Drawer */}
        <Dialog open={editDrawerOpen} onOpenChange={setEditDrawerOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="edit-timesheet-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-[#ffd108]" />
                Edit Timesheet
              </DialogTitle>
              <DialogDescription>
                {editingTimesheet?.operator_name} — {formatDate(editingTimesheet?.date)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Time <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2" data-testid="edit-ts-start-time">
                    <Select value={editForm.start_time?.split(':')[0] || '07'} onValueChange={(h) => { const m = editForm.start_time?.split(':')[1] || '00'; setEditForm({ ...editForm, start_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="flex items-center text-lg font-bold">:</span>
                    <Select value={editForm.start_time?.split(':')[1] || '00'} onValueChange={(m) => { const h = editForm.start_time?.split(':')[0] || '07'; setEditForm({ ...editForm, start_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Time <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2" data-testid="edit-ts-end-time">
                    <Select value={editForm.end_time?.split(':')[0] || '15'} onValueChange={(h) => { const m = editForm.end_time?.split(':')[1] || '00'; setEditForm({ ...editForm, end_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="flex items-center text-lg font-bold">:</span>
                    <Select value={editForm.end_time?.split(':')[1] || '00'} onValueChange={(m) => { const h = editForm.end_time?.split(':')[0] || '15'; setEditForm({ ...editForm, end_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Break (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  value={editForm.break_minutes}
                  onChange={(e) => setEditForm({ ...editForm, break_minutes: parseInt(e.target.value) || 0 })}
                  data-testid="edit-ts-break"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional notes..."
                  data-testid="edit-ts-notes"
                />
              </div>

              {/* Allowances */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="label-overline flex items-center gap-1">
                    <DollarSign className="h-3 w-3" /> Allowances
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setEditForm({
                      ...editForm,
                      allowances: [...editForm.allowances, { allowance_type_id: '', allowance_type_name: '', amount: 0, notes: '' }]
                    })}
                    data-testid="add-allowance-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </div>
                {editForm.allowances.length > 0 ? (
                  <div className="space-y-2">
                    {editForm.allowances.map((al, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-md border bg-muted/30" data-testid={`allowance-row-${idx}`}>
                        <Select
                          value={al.allowance_type_id || ''}
                          onValueChange={(v) => {
                            const atype = allowanceTypes.find(t => t.id === v);
                            const updated = [...editForm.allowances];
                            updated[idx] = {
                              ...updated[idx],
                              allowance_type_id: v,
                              allowance_type_name: atype?.name || '',
                              amount: updated[idx].amount || atype?.default_amount || 0,
                            };
                            setEditForm({ ...editForm, allowances: updated });
                          }}
                        >
                          <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`allowance-type-select-${idx}`}>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {allowanceTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">$</span>
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={al.amount}
                            onChange={(e) => {
                              const updated = [...editForm.allowances];
                              updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                              setEditForm({ ...editForm, allowances: updated });
                            }}
                            className="w-20 h-8 text-xs font-mono"
                            data-testid={`allowance-amount-${idx}`}
                          />
                        </div>
                        <Input
                          value={al.notes || ''}
                          onChange={(e) => {
                            const updated = [...editForm.allowances];
                            updated[idx] = { ...updated[idx], notes: e.target.value };
                            setEditForm({ ...editForm, allowances: updated });
                          }}
                          placeholder="Notes"
                          className="flex-1 h-8 text-xs"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500"
                          onClick={() => {
                            const updated = editForm.allowances.filter((_, i) => i !== idx);
                            setEditForm({ ...editForm, allowances: updated });
                          }}
                          data-testid={`remove-allowance-${idx}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No allowances added.</p>
                )}
              </div>

              {/* Time Blocks */}
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-overline flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Time Blocks
                  </Label>
                  {editingTimesheet?.operator_id && editingTimesheet?.date && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleAutoFillBlocks(editingTimesheet.operator_id, editingTimesheet.date, 'edit')}
                      data-testid="edit-autofill-blocks-btn"
                    >
                      <FileText className="h-3 w-3 mr-1" /> Auto-fill from Dockets
                    </Button>
                  )}
                </div>
                {/* Connection picker when multiple payroll connections */}
                <PayrollConnectionPicker payrollCtx={payrollCtx} testId="edit-payroll-connection-picker" />
                <TimeBlockEditor
                  blocks={editForm.time_blocks}
                  onBlocksChange={(blocks) => setEditForm({ ...editForm, time_blocks: blocks })}
                  connectedPlatform={payrollCtx.platform}
                  trackingCategories={payrollCtx.trackingCategories}
                  timesheetCategoryId={payrollCtx.timesheetCategoryId}
                  myobJobs={payrollCtx.myobJobs}
                  connectionId={payrollCtx.connectionId}
                />
              </div>

              <div className="space-y-2">
                <Label className="label-overline text-amber-600">Reason for Edit <span className="text-red-500">*</span></Label>
                <Textarea
                  value={editForm.edit_reason}
                  onChange={(e) => setEditForm({ ...editForm, edit_reason: e.target.value })}
                  rows={2}
                  placeholder="e.g., Operator reported incorrect break time, adjusted per supervisor..."
                  data-testid="edit-ts-reason"
                />
              </div>
              {editingTimesheet?.edit_history?.length > 0 && (
                <div className="space-y-2">
                  <Label className="label-overline flex items-center gap-1"><History className="h-3 w-3" /> Edit History</Label>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {editingTimesheet.edit_history.map((entry, i) => (
                      <div key={i} className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
                        <span className="font-medium">{entry.edited_by_name}</span> — {entry.reason}
                        <br />
                        <span className="text-[10px]">{formatDate(entry.edited_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDrawerOpen(false)}>Cancel</Button>
              <Button className="btn-volt" onClick={handleSaveEdit} data-testid="save-edit-ts-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Timesheet Drawer */}
        <Dialog open={createDrawerOpen} onOpenChange={setCreateDrawerOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="create-timesheet-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-[#ffd108]" />
                Add Timesheet
              </DialogTitle>
              <DialogDescription>
                Create a timesheet on behalf of an operator
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Operator <span className="text-red-500">*</span></Label>
                <Select value={createForm.operator_id} onValueChange={handleCreateOperatorChange}>
                  <SelectTrigger data-testid="create-ts-operator">
                    <SelectValue placeholder="Select operator..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Date <span className="text-red-500">*</span></Label>
                <DatePicker
                  value={createForm.date}
                  onChange={(d) => setCreateForm({ ...createForm, date: d })}
                  className="w-full"
                  testId="create-ts-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Job</Label>
                <Select value={createForm.job_id} onValueChange={(v) => {
                  const job = availableJobs.find(j => j.id === v);
                  setCreateForm(prev => ({
                    ...prev,
                    job_id: v,
                    ...(job?.start_time ? { start_time: job.start_time } : {}),
                    ...(job?.end_time ? { end_time: job.end_time } : {}),
                  }));
                }}>
                  <SelectTrigger data-testid="create-ts-job">
                    <SelectValue placeholder="Select job..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableJobs.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        #{j.job_number} — {j.description?.substring(0, 50) || j.equipment_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Start Time <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2" data-testid="create-ts-start-time">
                    <Select value={createForm.start_time?.split(':')[0] || '07'} onValueChange={(h) => { const m = createForm.start_time?.split(':')[1] || '00'; setCreateForm({ ...createForm, start_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="flex items-center text-lg font-bold">:</span>
                    <Select value={createForm.start_time?.split(':')[1] || '00'} onValueChange={(m) => { const h = createForm.start_time?.split(':')[0] || '07'; setCreateForm({ ...createForm, start_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">End Time <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2" data-testid="create-ts-end-time">
                    <Select value={createForm.end_time?.split(':')[0] || '15'} onValueChange={(h) => { const m = createForm.end_time?.split(':')[1] || '00'; setCreateForm({ ...createForm, end_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="HH" /></SelectTrigger>
                      <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="flex items-center text-lg font-bold">:</span>
                    <Select value={createForm.end_time?.split(':')[1] || '00'} onValueChange={(m) => { const h = createForm.end_time?.split(':')[0] || '15'; setCreateForm({ ...createForm, end_time: `${h}:${m}` }); }}>
                      <SelectTrigger className="w-20 font-mono"><SelectValue placeholder="MM" /></SelectTrigger>
                      <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Break (minutes)</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.break_minutes}
                  onChange={(e) => setCreateForm({ ...createForm, break_minutes: parseInt(e.target.value) || 0 })}
                  data-testid="create-ts-break"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional notes..."
                  data-testid="create-ts-notes"
                />
              </div>
              {/* Time Blocks */}
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="label-overline flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Time Blocks
                  </Label>
                  {createForm.operator_id && createForm.date && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleAutoFillBlocks(createForm.operator_id, createForm.date, 'create')}
                      data-testid="create-autofill-blocks-btn"
                    >
                      <FileText className="h-3 w-3 mr-1" /> Auto-fill from Dockets
                    </Button>
                  )}
                </div>
                <PayrollConnectionPicker payrollCtx={payrollCtx} testId="create-payroll-connection-picker" />
                <TimeBlockEditor
                  blocks={createForm.time_blocks}
                  onBlocksChange={(blocks) => setCreateForm({ ...createForm, time_blocks: blocks })}
                  connectedPlatform={payrollCtx.platform}
                  trackingCategories={payrollCtx.trackingCategories}
                  timesheetCategoryId={payrollCtx.timesheetCategoryId}
                  myobJobs={payrollCtx.myobJobs}
                  connectionId={payrollCtx.connectionId}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDrawerOpen(false)}>Cancel</Button>
              <Button className="btn-volt" onClick={handleSaveCreate} data-testid="save-create-ts-btn">Create Timesheet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </>
        )}
      </div>
    </FeatureGate>
  );
};

export default Timesheets;
